import React, { useState, useEffect } from 'react';
import {
  UserIcon,
  IdentificationIcon,
  BriefcaseIcon,
  ShieldCheckIcon,
  CalendarDaysIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import addEmployee from './addEmployee';
import updateEmployee from './updateEmployee';
import toast from '../../utils/toast';
import Button from '../common/Button';

function EmployeeForm({ employee, onSuccess, onCancel, layout = 'modal' }) {
  const isEdit = Boolean(employee);
  const [formData, setFormData] = useState({
    empcode: '',
    firstName: '',
    lastName: '',
    dateOfJoining: '',
    phoneNo: '',
    emailAddress: '',
    address: '',
    role: 'employee',
    designation: '',
    employmentStatus: 'active',
    leavingDate: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (employee) {
      setFormData({
        empcode: employee.empcode || '',
        firstName: employee.first_name || '',
        lastName: employee.last_name || '',
        dateOfJoining: employee.date_of_joining
          ? String(employee.date_of_joining).slice(0, 10)
          : '',
        phoneNo: employee.phone_no || '',
        emailAddress: employee.email_address || '',
        address: employee.address || '',
        role: employee.role || 'employee',
        designation: employee.designation || '',
        employmentStatus:
          (employee.is_active ?? employee.isActive ?? employee.status === 'active') === false
            ? 'inactive'
            : 'active',
        leavingDate: employee.leaving_date ? String(employee.leaving_date).slice(0, 10) : ''
      });
    } else {
      setFormData({
        empcode: '',
        firstName: '',
        lastName: '',
        dateOfJoining: '',
        phoneNo: '',
        emailAddress: '',
        address: '',
        role: 'employee',
        designation: '',
        employmentStatus: 'active',
        leavingDate: ''
      });
    }
    setErrors({});
  }, [employee]);

  const validateForm = () => {
    const newErrors = {};
// by me 
    if (!formData.empcode.trim()) {
      newErrors.empcode = 'Employee code is required';
    } else if (formData.empcode.trim().length < 4) {
      newErrors.empcode = 'Employee code must be at least 4 characters';
    }
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
    }

    if (!formData.emailAddress.trim()) {
      newErrors.emailAddress = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailAddress)) {
      newErrors.emailAddress = 'Invalid email address format';
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    if (!formData.designation) {
      newErrors.designation = 'Designation is required';
    }

    if (!formData.dateOfJoining) {
      newErrors.dateOfJoining = 'Date of joining is required';
    } else {
      const joiningDate = new Date(formData.dateOfJoining);
      const today = new Date();
      if (joiningDate > today) {
        newErrors.dateOfJoining = 'Date of joining cannot be in the future';
      }
    }

    if (formData.phoneNo && formData.phoneNo.trim()) {
      if (formData.phoneNo.length > 15) {
        newErrors.phoneNo = 'Phone number must not exceed 15 characters';
      } else if (!/^[0-9+\-\s()]+$/.test(formData.phoneNo)) {
        newErrors.phoneNo = 'Invalid phone number format';
      }
    }
    if (formData.employmentStatus === 'inactive' && !formData.leavingDate) {
      newErrors.leavingDate = 'Leaving date is required for inactive employees';
    }
    if (formData.leavingDate && Number.isNaN(new Date(formData.leavingDate).getTime())) {
      newErrors.leavingDate = 'Invalid leaving date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'employmentStatus' && value === 'active') {
        next.leavingDate = '';
      }
      return next;
    });
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the validation errors');
      return;
    }

    setIsSubmitting(true);

    const employeeData = {
      empcode: formData.empcode.trim() || null,
      first_name: formData.firstName.trim(),
      last_name: formData.lastName.trim() || null,
      date_of_joining: formData.dateOfJoining,
      phone_no: formData.phoneNo.trim() || null,
      email_address: formData.emailAddress.trim().toLowerCase(),
      address: formData.address.trim() || null,
      role: formData.role,
      designation: formData.designation,
      teamID: employee?.team_id || null,
      is_active: formData.employmentStatus === 'active',
      leaving_date:
        formData.employmentStatus === 'inactive' ? formData.leavingDate || null : null
    };

    try {
      if (employee) {
        await updateEmployee(employeeData);
        toast.success('Employee updated successfully!');
      } else {
        await addEmployee(employeeData);
        toast.success('Employee added successfully!');
      }
      onSuccess();
    } catch (error) {
      console.error('Error saving employee:', error);

      if (error.response?.data?.errors) {
        const backendErrors = error.response.data.errors;
        if (Array.isArray(backendErrors)) {
          backendErrors.forEach((err) => toast.error(err));
        } else {
          toast.error(backendErrors);
        }
      } else {
        const errorMessage =
          error.response?.data?.message || `Failed to ${employee ? 'update' : 'add'} employee`;
        toast.error(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = (field) =>
    `mt-1.5 block w-full rounded-lg border px-3 py-2.5 text-sm text-slate-900 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
      errors[field] ? 'border-red-400 bg-red-50/50' : 'border-slate-200 bg-white'
    }`;

  const labelClass = 'block text-xs font-semibold uppercase tracking-wider text-slate-500';

  const isActiveRecord = formData.employmentStatus === 'active';

  const displayName = [formData.firstName, formData.lastName].filter(Boolean).join(' ') || 'New employee';

  const formBody = (
    <div
      className={
        layout === 'inline'
          ? 'rounded-2xl border border-slate-200/80 bg-slate-50/90 shadow-sm'
          : 'max-h-[90vh] overflow-y-auto rounded-xl bg-white shadow-xl'
      }
    >
      <div className="border-b border-slate-100 bg-white px-4 py-4 sm:px-8 sm:py-5">
        <nav className="mb-4 flex flex-wrap items-center gap-1 text-sm text-slate-500">
          <button
            type="button"
            onClick={onCancel}
            className="font-medium text-slate-600 transition hover:text-blue-700"
          >
            Employee Directory
          </button>
          <ChevronRightIcon className="h-4 w-4 shrink-0 text-slate-300" aria-hidden />
          {isEdit ? (
            <>
              <span className="font-medium text-slate-800">{displayName}</span>
              <ChevronRightIcon className="h-4 w-4 shrink-0 text-slate-300" aria-hidden />
              <span className="font-medium text-slate-800">Update record</span>
            </>
          ) : (
            <>
              <span className="font-medium text-slate-800">Add employee</span>
            </>
          )}
        </nav>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            {isEdit ? 'Update employee record' : 'Add employee'}
          </h1>
          <div className="flex flex-wrap gap-3">
            <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" form="employee-record-form" loading={isSubmitting}>
              Save changes
            </Button>
          </div>
        </div>
      </div>

      <form id="employee-record-form" onSubmit={handleSubmit} className="p-4 sm:p-8" noValidate>
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="space-y-8 lg:col-span-2">
            <section className="rounded-xl border border-slate-100 bg-white p-6 shadow-sm">
              <h2 className="mb-6 flex items-center gap-2 text-base font-semibold text-slate-900">
                <UserIcon className="h-6 w-6 text-blue-600" aria-hidden />
                Personal information
              </h2>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className={labelClass} htmlFor="emp-firstName">
                    First name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="emp-firstName"
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={inputClass('firstName')}
                    autoComplete="given-name"
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
                  )}
                </div>
                <div>
                  <label className={labelClass} htmlFor="emp-lastName">
                    Last name
                  </label>
                  <input
                    id="emp-lastName"
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={inputClass('lastName')}
                    autoComplete="family-name"
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
                  )}
                </div>
              </div>
            </section>

            <section className="rounded-xl border border-slate-100 bg-white p-6 shadow-sm">
              <h2 className="mb-6 flex items-center gap-2 text-base font-semibold text-slate-900">
                <IdentificationIcon className="h-6 w-6 text-blue-600" aria-hidden />
                Contact details
              </h2>
              <div className="space-y-6">
                <div>
                  <label className={labelClass} htmlFor="emp-phone">
                    Phone number
                  </label>
                  <div className="relative mt-1.5">
                    <PhoneIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                    <input
                      id="emp-phone"
                      type="tel"
                      name="phoneNo"
                      value={formData.phoneNo}
                      onChange={handleChange}
                      maxLength={15}
                      className={`${inputClass('phoneNo')} pl-10`}
                      placeholder="Optional"
                      autoComplete="tel"
                    />
                  </div>
                  {errors.phoneNo && (
                    <p className="mt-1 text-sm text-red-600">{errors.phoneNo}</p>
                  )}
                </div>

                <div>
                  <div className="flex items-baseline justify-between gap-2">
                    <label className={labelClass} htmlFor="emp-email">
                      Email address <span className="text-red-500">*</span>
                    </label>
                    {isEdit && (
                      <span className="text-[10px] font-bold uppercase tracking-wide text-blue-600">
                        Primary
                      </span>
                    )}
                  </div>
                  <div className="relative mt-1.5">
                    <EnvelopeIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                    <input
                      id="emp-email"
                      type="email"
                      name="emailAddress"
                      value={formData.emailAddress}
                      onChange={handleChange}
                      disabled={isEdit}
                      className={`${inputClass('emailAddress')} pl-10 ${
                        isEdit ? 'cursor-not-allowed bg-blue-50/80 text-slate-700' : ''
                      }`}
                      autoComplete="email"
                    />
                  </div>
                  {errors.emailAddress && (
                    <p className="mt-1 text-sm text-red-600">{errors.emailAddress}</p>
                  )}
                  {isEdit && (
                    <p className="mt-1.5 text-xs text-slate-500">
                      System email cannot be changed here.
                    </p>
                  )}
                </div>

                <div>
                  <label className={labelClass} htmlFor="emp-address">
                    Residential address
                  </label>
                  <div className="relative mt-1.5">
                    <MapPinIcon className="pointer-events-none absolute left-3 top-3 h-5 w-5 text-slate-400" />
                    <textarea
                      id="emp-address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      rows={4}
                      className={`${inputClass('address')} resize-y pl-10`}
                      placeholder="Optional"
                      autoComplete="street-address"
                    />
                  </div>
                  {errors.address && (
                    <p className="mt-1 text-sm text-red-600">{errors.address}</p>
                  )}
                </div>
              </div>
            </section>
          </div>

          <div className="space-y-8 lg:col-span-1">
            <section className="rounded-xl border border-slate-100 bg-white p-6 shadow-sm">
              <h2 className="mb-6 flex items-center gap-2 text-base font-semibold text-slate-900">
                <BriefcaseIcon className="h-6 w-6 text-blue-600" aria-hidden />
                Job details
              </h2>
              <div className="space-y-6">
                <div>
                  <label className={labelClass} htmlFor="emp-code">
                    Employee code <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="emp-code"
                    type="text"
                    name="empcode"
                    value={formData.empcode}
                    onChange={handleChange}
                    className={inputClass('empcode')}
                    placeholder="Required"
                  />
                  {errors.empcode && (
                    <p className="mt-1 text-sm text-red-600">{errors.empcode}</p>
                  )}
                </div>

                <div>
                  <label className={labelClass} htmlFor="emp-doj">
                    Date of joining <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="emp-doj"
                    type="date"
                    name="dateOfJoining"
                    value={formData.dateOfJoining}
                    onChange={handleChange}
                    max={new Date().toISOString().split('T')[0]}
                    className={inputClass('dateOfJoining')}
                  />
                  {errors.dateOfJoining && (
                    <p className="mt-1 text-sm text-red-600">{errors.dateOfJoining}</p>
                  )}
                </div>

                <div>
                  <label className={labelClass} htmlFor="emp-role">
                    Role <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="emp-role"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    className={inputClass('role')}
                  >
                    <option value="">Select role</option>
                    <option value="employee">Employee</option>
                    <option value="team_lead">Team lead</option>
                    <option value="manager">Manager</option>
                    <option value="HR">HR</option>
                    <option value="accountant">Accountant</option>
                    <option value="admin">Admin</option>
                  </select>
                  {errors.role && <p className="mt-1 text-sm text-red-600">{errors.role}</p>}
                </div>

                <div>
                  <label className={labelClass} htmlFor="emp-status">
                    Employment status
                  </label>
                  <select
                    id="emp-status"
                    name="employmentStatus"
                    value={formData.employmentStatus}
                    onChange={handleChange}
                    className={inputClass('employmentStatus')}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                <div>
                  <label className={labelClass} htmlFor="emp-designation">
                    Designation <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="emp-designation"
                    name="designation"
                    value={formData.designation}
                    onChange={handleChange}
                    className={inputClass('designation')}
                  >
                    <option value="">Select designation</option>
                    <option value="Devops Engineer">Devops Engineer</option>
                    <option value="Software Developer Engineer">Software Developer Engineer</option>
                    <option value="DevOps Intern">DevOps Intern</option>
                    <option value="Software Developer Intern">Software Developer Intern</option>
                    <option value="Designer">Designer</option>
                    <option value="HR">HR</option>
                    <option value="Accountant">Accountant</option>
                    <option value="Senior Accountant">Senior Accountant</option>
                    <option value="Finance Executive">Finance Executive</option>
                    <option value="Business Development Intern">Business Development Intern</option>
                    <option value="Business Development Executive">Business Development Executive</option>
                  </select>
                  {errors.designation && (
                    <p className="mt-1 text-sm text-red-600">{errors.designation}</p>
                  )}
                </div>

                {formData.employmentStatus === 'inactive' && (
                  <div>
                    <label className={labelClass} htmlFor="emp-leavingDate">
                      Leaving date <span className="text-red-500">*</span>
                    </label>
                    <div className="relative mt-1.5">
                      <CalendarDaysIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                      <input
                        id="emp-leavingDate"
                        type="date"
                        name="leavingDate"
                        value={formData.leavingDate}
                        onChange={handleChange}
                        className={`${inputClass('leavingDate')} pl-10`}
                      />
                    </div>
                    {errors.leavingDate && (
                      <p className="mt-1 text-sm text-red-600">{errors.leavingDate}</p>
                    )}
                  </div>
                )}
              </div>
            </section>

            <div className="rounded-xl border border-blue-100 bg-gradient-to-b from-blue-50/90 to-slate-50 p-6 shadow-sm">
              <h2 className="mb-3 flex items-center gap-2 text-base font-semibold text-slate-900">
                <ShieldCheckIcon className="h-6 w-6 text-blue-600" aria-hidden />
                {isEdit ? (isActiveRecord ? 'Active employee' : 'Inactive employee') : 'New record'}
              </h2>
              <p className="text-sm leading-relaxed text-slate-600">
                {isEdit
                  ? isActiveRecord
                    ? 'This employee currently has active directory access. To deactivate or remove them, use the directory actions (delete / soft delete).'
                    : 'This employee is inactive. Restore or manage access from the employee directory as your role allows.'
                  : 'After saving, the employee receives credentials per your organization setup. You can edit their profile anytime from the directory.'}
              </p>
            </div>
          </div>
        </div>
      </form>
    </div>
  );

  if (layout === 'modal') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-[1px]">
        <div className="w-full max-w-5xl">{formBody}</div>
      </div>
    );
  }

  return <div className="w-full">{formBody}</div>;
}

export default EmployeeForm;
